/**
 * Bounds static block body reads on the capture page. Each unread response
 * holds ~2 MB of the capture lambda's /tmp, and at ~150 in flight every
 * further request fails. Iframe cards are already paced by mount admission;
 * static blocks have no admission stage, so their reads queue here.
 */

const MAX_IN_FLIGHT = 24;
// A stalled read gives its slot back so it cannot wedge the queue.
const STALL_MS = 30_000;

let inFlight = 0;
const queue: Array<() => void> = [];

function next(): void {
  while (inFlight < MAX_IN_FLIGHT && queue.length > 0) queue.shift()!();
}

/**
 * Calls `onGranted` once a slot is free. The returned release frees the slot,
 * or leaves the queue if it was never granted; it is idempotent.
 */
export function takeCaptureReadSlot(onGranted: () => void): () => void {
  let state: "queued" | "granted" | "done" = "queued";
  let timer: ReturnType<typeof setTimeout> | undefined;
  const grant = () => {
    state = "granted";
    inFlight++;
    timer = setTimeout(release, STALL_MS);
    onGranted();
  };
  function release() {
    if (state === "queued") {
      const i = queue.indexOf(grant);
      if (i >= 0) queue.splice(i, 1);
    } else if (state === "granted") {
      clearTimeout(timer);
      inFlight--;
      next();
    }
    state = "done";
  }
  queue.push(grant);
  next();
  return release;
}
